import { useMemo } from 'react';
import PropTypes from 'prop-types';
import Button from 'components/UI/Button';
import Input from 'components/UI/Input';
import Select from 'components/UI/Select';
import { ReactComponent as InfoIcon } from 'assets/icons/digital-loan-info.svg';
import { MOCK_PRODUCT_CONDITION_OPTIONS } from 'modules/digitalLoan/mocks';
import {
  MANUAL_CATALOG_LEVELS,
  MANUAL_NAME_MAX_LENGTH,
  MANUAL_OTHER_NAME,
} from 'constants/digitalLoanPhase2';
import { formatMoneyInput, parseMoneyInput } from 'routes/DigitalLoan/utils/moneyInput';
import { areManualCatalogValuesComplete } from 'routes/DigitalLoan/utils/catalogOtherOption';

const ManualCatalogDetailScreen = ({
  literals,
  startLevel,
  values,
  productCondition,
  estimatedValue,
  onValuesChange,
  onConditionChange,
  onEstimatedValueChange,
  onNext,
  onBack,
}) => {
  const subfamilyLocked = startLevel === MANUAL_CATALOG_LEVELS.FAMILY
    || startLevel === MANUAL_CATALOG_LEVELS.SUBFAMILY;
  const showFamily = startLevel === MANUAL_CATALOG_LEVELS.FAMILY;
  const showSubfamily = startLevel === MANUAL_CATALOG_LEVELS.FAMILY
    || startLevel === MANUAL_CATALOG_LEVELS.SUBFAMILY;
  const showArticle = startLevel === MANUAL_CATALOG_LEVELS.FAMILY
    || startLevel === MANUAL_CATALOG_LEVELS.SUBFAMILY
    || startLevel === MANUAL_CATALOG_LEVELS.ARTICLE;
  const showBrand = showArticle || startLevel === MANUAL_CATALOG_LEVELS.BRAND;

  const conditionOptions = useMemo(() => (
    MOCK_PRODUCT_CONDITION_OPTIONS.map((option) => ({
      value: option.value,
      label: literals.conditions[option.labelKey],
    }))
  ), [literals.conditions]);

  const hasEstimatedValue = estimatedValue !== ''
    && estimatedValue != null
    && Number.isFinite(Number(estimatedValue))
    && Number(estimatedValue) > 0;

  const canContinue = areManualCatalogValuesComplete(startLevel, values)
    && !!productCondition
    && hasEstimatedValue;

  return (
    <div className='full-screen-container digital-loan-model-detail'>
      <div className='screen-content'>
        <h1 className='digital-loan-phase2__title'>{literals.title}</h1>
        <p className='digital-loan-phase2__body'>{literals.intro1}</p>
        <p className='digital-loan-phase2__body mb-4'>{literals.intro2}</p>

        {showFamily && (
          <Input
            className='mb-4'
            name='family'
            label={literals.familyLabel}
            placeholder={literals.familyPlaceholder}
            value={values.familyName || ''}
            onChange={(familyName) => onValuesChange({ familyName })}
            maxLength={MANUAL_NAME_MAX_LENGTH}
            required
          />
        )}

        {showSubfamily && (
          <Input
            className='mb-4'
            name='subfamily'
            label={literals.subFamilyLabel}
            placeholder={literals.subFamilyPlaceholder}
            value={subfamilyLocked ? MANUAL_OTHER_NAME : (values.subfamilyName || '')}
            onChange={(subfamilyName) => {
              if (subfamilyLocked) return;
              onValuesChange({ subfamilyName });
            }}
            maxLength={MANUAL_NAME_MAX_LENGTH}
            readOnly={subfamilyLocked}
          />
        )}

        {showArticle && (
          <Input
            className='mb-4'
            name='article'
            label={literals.articleLabel}
            placeholder={literals.articlePlaceholder}
            value={values.articleName || ''}
            onChange={(articleName) => onValuesChange({ articleName })}
            maxLength={MANUAL_NAME_MAX_LENGTH}
            required
          />
        )}

        {showBrand && (
          <Input
            className='mb-4'
            name='brand'
            label={literals.brandLabel}
            placeholder={literals.brandPlaceholder}
            value={values.brandName || ''}
            onChange={(brandName) => onValuesChange({ brandName })}
            maxLength={MANUAL_NAME_MAX_LENGTH}
            required
          />
        )}

        <Input
          className='mb-4'
          name='model'
          label={literals.modelLabel}
          placeholder={literals.modelPlaceholder}
          value={values.modelName || ''}
          onChange={(modelName) => onValuesChange({ modelName })}
          maxLength={MANUAL_NAME_MAX_LENGTH}
          required
        />

        <Select
          className='mb-4'
          name='condition'
          label={literals.conditionLabel}
          placeholder={literals.conditionPlaceholder}
          options={conditionOptions}
          value={productCondition}
          onChange={onConditionChange}
          required
        />
        <Input
          className='mb-1'
          name='estimatedValue'
          label={literals.estimatedValueLabel}
          placeholder={literals.estimatedValuePlaceholder}
          value={formatMoneyInput(estimatedValue)}
          onChange={(raw) => onEstimatedValueChange(parseMoneyInput(raw))}
          inputMode='numeric'
          required
        />
        <p className='digital-loan-model-detail__hint'>
          <InfoIcon className='digital-loan-model-detail__hint-icon' aria-hidden='true' />
          <span>{literals.estimatedValueHint}</span>
        </p>
      </div>
      <div className='screen-actions'>
        <Button text={literals.next} disabled={!canContinue} onClick={onNext} />
        <Button text={literals.back} type='secondary' onClick={onBack} />
      </div>
    </div>
  );
};

ManualCatalogDetailScreen.propTypes = {
  literals: PropTypes.object.isRequired,
  startLevel: PropTypes.oneOf(Object.values(MANUAL_CATALOG_LEVELS)).isRequired,
  values: PropTypes.shape({
    familyName: PropTypes.string,
    subfamilyName: PropTypes.string,
    articleName: PropTypes.string,
    brandName: PropTypes.string,
    modelName: PropTypes.string,
  }).isRequired,
  productCondition: PropTypes.string,
  estimatedValue: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  onValuesChange: PropTypes.func.isRequired,
  onConditionChange: PropTypes.func.isRequired,
  onEstimatedValueChange: PropTypes.func.isRequired,
  onNext: PropTypes.func.isRequired,
  onBack: PropTypes.func.isRequired,
};

export default ManualCatalogDetailScreen;
